(() => {
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function whenGsapReady(callback) {
    if (typeof window.gsap !== "undefined" && typeof window.ScrollTrigger !== "undefined") {
      callback(window.gsap, window.ScrollTrigger);
      return;
    }
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      if (typeof window.gsap !== "undefined" && typeof window.ScrollTrigger !== "undefined") {
        clearInterval(timer);
        callback(window.gsap, window.ScrollTrigger);
      } else if (tries > 40) {
        clearInterval(timer);
      }
    }, 50);
  }

  const BUTTON_EXCLUDE =
    ".button--tertiary, .button-close, .cart-remove-button, .quantity-popover__info-button, .product__xr-button, .shopify-payment-button__button, .gift-card__copy-button, .disclosure__button, .password-button";

  function isEligibleButton(el) {
    if (!el || el.dataset.gsapInvert === "true") return false;

    // Normalizza classi legacy homepage (image-with-text)
    if (el.classList.contains("btn--primary") && !el.classList.contains("button--primary")) {
      el.classList.add("button--primary");
    }
    if (el.classList.contains("btn--secondary") && !el.classList.contains("button--secondary")) {
      el.classList.add("button--secondary");
    }
    // Bottone generico .button senza variante → primary
    if (
      el.classList.contains("button") &&
      !el.classList.contains("button--primary") &&
      !el.classList.contains("button--secondary") &&
      !el.classList.contains("button--tertiary")
    ) {
      el.classList.add("button--primary");
    }

    if (!(el.classList.contains("button--primary") || el.classList.contains("button--secondary"))) {
      return false;
    }
    if (el.matches(BUTTON_EXCLUDE)) return false;
    if (el.disabled || el.getAttribute("aria-disabled") === "true") return false;
    if (el.closest("shopify-accelerated-checkout, .shopify-payment-button")) return false;
    return true;
  }

  function enhanceButtonMarkup(button) {
    button.classList.add("button--gsap-invert");

    let bg = button.querySelector(":scope > .button__bg");
    if (!bg) {
      bg = document.createElement("span");
      bg.className = "button__bg";
      bg.setAttribute("aria-hidden", "true");
      button.insertBefore(bg, button.firstChild);
    }

    let label = button.querySelector(":scope > .button__label");
    if (!label) {
      label = document.createElement("span");
      label.className = "button__label";
      const nodes = Array.from(button.childNodes).filter((node) => {
        return !(node.nodeType === 1 && node.classList && node.classList.contains("button__bg"));
      });
      nodes.forEach((node) => label.appendChild(node));
      button.appendChild(label);
    }

    return { bg, label };
  }

  function parseRgb(color) {
    if (!color) return null;
    if (String(color).trim().startsWith("#")) {
      const hex = String(color).trim().slice(1);
      const full =
        hex.length === 3
          ? hex
              .split("")
              .map((c) => c + c)
              .join("")
          : hex;
      if (full.length !== 6) return null;
      return {
        r: parseInt(full.slice(0, 2), 16),
        g: parseInt(full.slice(2, 4), 16),
        b: parseInt(full.slice(4, 6), 16)
      };
    }
    const match = String(color).match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?/i);
    if (!match) return null;
    return {
      r: Number(match[1]),
      g: Number(match[2]),
      b: Number(match[3]),
      a: match[4] !== undefined ? Number(match[4]) : 1
    };
  }

  function isTransparentColor(color) {
    const rgb = parseRgb(color);
    if (!rgb) return true;
    return rgb.a === 0;
  }

  function isLightColor(color) {
    const rgb = parseRgb(color);
    if (!rgb) return false;
    const lum = (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
    return lum > 0.55;
  }

  function colorsClose(a, b, threshold = 42) {
    const ca = parseRgb(a);
    const cb = parseRgb(b);
    if (!ca || !cb) return false;
    return (
      Math.abs(ca.r - cb.r) <= threshold &&
      Math.abs(ca.g - cb.g) <= threshold &&
      Math.abs(ca.b - cb.b) <= threshold
    );
  }

  function getEffectiveBackground(el) {
    let node = el.parentElement;
    while (node && node !== document.documentElement) {
      const styles = getComputedStyle(node);
      const bg = styles.backgroundColor;
      if (!isTransparentColor(bg)) {
        return bg;
      }
      // Sezioni con foto / overlay scuro: tratta il contesto come scuro
      if (
        node.matches(
          ".hero-banner-slider, .hero-banner-slider__slide, .banner, .image-banner, .slideshow, .video-section, .collection-hero, .split-hero-image-wrapper, .split-hero-overlay, .split-hero-link"
        ) ||
        node.classList.contains("banner__media")
      ) {
        return "rgb(28, 28, 28)";
      }
      if (
        styles.backgroundImage &&
        styles.backgroundImage !== "none" &&
        (node.matches(
          ".hero-banner-slider, .hero-banner-slider__slide, .banner, .image-banner, .slideshow, .video-section, .collection-hero"
        ) ||
          node.classList.contains("banner__media"))
      ) {
        return "rgb(28, 28, 28)";
      }
      node = node.parentElement;
    }
    return "rgb(255, 255, 255)";
  }

  function getInvertColors(button) {
    // Forza contesto scuro su hero / immagine split (proposta: bottoni bianchi)
    const forceDark =
      Boolean(button.closest(".hero-banner-slider")) ||
      Boolean(button.closest(".split-hero-link, .split-hero-image-wrapper")) ||
      button.classList.contains("split-hero-cta");

    // Footer newsletter: sempre nero su chiaro
    const forceLight =
      Boolean(button.closest(".footer__newsletter, .footer-premium")) &&
      !forceDark;

    const contextBg = forceDark
      ? "rgb(28, 28, 28)"
      : forceLight
        ? "rgb(255, 255, 255)"
        : getEffectiveBackground(button);

    const onDark = forceDark || !isLightColor(contextBg);

    button.classList.remove("button--gsap-outline");
    button.style.removeProperty("--gsap-outline-color");
    button.style.removeProperty("--gsap-outline-fill");

    if (onDark) {
      button.classList.add("button--gsap-on-dark");
      button.classList.remove("button--gsap-on-light");
      // Pieno bianco → outline bianco
      return {
        solidColor: "#ffffff",
        labelFrom: "#111111",
        labelTo: "#ffffff"
      };
    }

    button.classList.add("button--gsap-on-light");
    button.classList.remove("button--gsap-on-dark");
    // Pieno nero → outline nero
    return {
      solidColor: "#111111",
      labelFrom: "#ffffff",
      labelTo: "#111111"
    };
  }

  function initInvertButtons(gsap) {
    const buttons = document.querySelectorAll(
      "a.button, button.button, span.button.button--primary, span.button.button--secondary, .split-hero-cta, .footer__newsletter-form button"
    );
    buttons.forEach((button) => {
      if (!isEligibleButton(button)) return;

      const { bg, label } = enhanceButtonMarkup(button);
      if (!bg || !label) return;

      const { solidColor, labelFrom, labelTo } = getInvertColors(button);

      // Pieno a riposo (scaleX 1); all’hover si ritira e resta l’outline
      gsap.set(bg, {
        scaleX: 1,
        skewX: 0,
        transformOrigin: "left center",
        backgroundColor: solidColor
      });
      gsap.set(label, { color: labelFrom });

      const enterTl = gsap.timeline({ paused: true });
      enterTl
        .set(bg, { transformOrigin: "right center" })
        .to(
          bg,
          {
            scaleX: 0,
            skewX: 8,
            duration: 0.55,
            ease: "expo.inOut"
          },
          0
        )
        .to(
          label,
          {
            color: labelTo,
            duration: 0.28,
            ease: "power2.out"
          },
          0.12
        );

      const leaveTl = gsap.timeline({ paused: true });
      leaveTl
        .set(bg, { transformOrigin: "left center" })
        .to(
          bg,
          {
            scaleX: 1,
            skewX: 0,
            duration: 0.5,
            ease: "expo.inOut"
          },
          0
        )
        .to(
          label,
          {
            color: labelFrom,
            duration: 0.28,
            ease: "power2.out"
          },
          0.05
        );

      const playEnter = () => {
        if (reduceMotion) {
          button.classList.add("is-hovered");
          return;
        }
        button.classList.add("is-hovered");
        leaveTl.pause(0);
        enterTl.restart();
      };

      const playLeave = () => {
        if (reduceMotion) {
          button.classList.remove("is-hovered");
          return;
        }
        button.classList.remove("is-hovered");
        enterTl.pause(0);
        gsap.set(bg, { scaleX: 0, skewX: 8, transformOrigin: "left center" });
        leaveTl.restart();
      };

      // Se il CTA è dentro un link-immagine, l’hover parte dal wrapper
      const hoverProxy =
        button.closest(".split-hero-link") ||
        button.closest("[data-gsap-button-hover]") ||
        button;

      if (hoverProxy !== button) {
        button._gsapPlayEnter = playEnter;
        button._gsapPlayLeave = playLeave;
        if (hoverProxy.dataset.gsapBtnProxy !== "true") {
          hoverProxy.addEventListener("mouseenter", () => {
            const cta = hoverProxy.querySelector(".button--gsap-invert, .split-hero-cta");
            if (cta && typeof cta._gsapPlayEnter === "function") cta._gsapPlayEnter();
          });
          hoverProxy.addEventListener("mouseleave", () => {
            const cta = hoverProxy.querySelector(".button--gsap-invert, .split-hero-cta");
            if (cta && typeof cta._gsapPlayLeave === "function") cta._gsapPlayLeave();
          });
          hoverProxy.dataset.gsapBtnProxy = "true";
        }
      } else {
        button.addEventListener("mouseenter", playEnter);
        button.addEventListener("mouseleave", playLeave);
      }

      button.dataset.gsapInvert = "true";
    });
  }

  function animateHeroSlide(gsap, slide) {
    if (!slide || reduceMotion) return;
    const title = slide.querySelector(".js-hero-title");
    const buttons = slide.querySelectorAll(".button--gsap-invert");
    const media = slide.querySelector(".hero-banner-slider__image");

    const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
    if (media) {
      // Solo scale: filter/opacity sull’img creano stacking che copre la velina
      gsap.fromTo(
        media,
        { scale: 1.1 },
        { scale: 1, duration: 1.55, ease: "power2.out" }
      );
    }
    if (title) {
      tl.fromTo(title, { y: 40, opacity: 0 }, { y: 0, opacity: 1, duration: 0.95 }, 0.12);
    }
    if (buttons.length) {
      tl.fromTo(
        buttons,
        { y: 26, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.7, stagger: 0.1 },
        0.32
      );
    }
  }

  function initHeroEntrance(gsap) {
    const activeSlide =
      document.querySelector(".hero-banner-swiper .swiper-slide-active") ||
      document.querySelector(".hero-banner-swiper .swiper-slide");
    animateHeroSlide(gsap, activeSlide);

    document.querySelectorAll(".hero-banner-swiper").forEach((container) => {
      if (container.dataset.gsapSlideBound === "true") return;
      container.dataset.gsapSlideBound = "true";
      container.addEventListener("semenzato:heroSlideChange", (event) => {
        animateHeroSlide(gsap, event.detail && event.detail.slide);
      });
    });
  }

  function initScrollMotions(gsap, ScrollTrigger) {
    gsap.registerPlugin(ScrollTrigger);
    if (reduceMotion) return;

    gsap.utils.toArray(".intro-categories__header").forEach((el) => {
      gsap.from(el, {
        y: 42,
        opacity: 0,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          toggleActions: "play none none reverse"
        }
      });
    });

    gsap.utils.toArray(".intro-categories__item").forEach((el, i) => {
      gsap.from(el, {
        y: 64,
        opacity: 0,
        duration: 1.05,
        delay: (i % 3) * 0.09,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 88%",
          toggleActions: "play none none reverse"
        }
      });
    });

    const splitHero = document.querySelector(".split-hero-collection");
    if (splitHero) {
      const image = splitHero.querySelector(".split-hero-image");
      const featured = splitHero.querySelector(".featured-collection");
      if (image) {
        gsap.fromTo(
          image,
          { scale: 1.14, yPercent: 5 },
          {
            scale: 1,
            yPercent: 0,
            ease: "none",
            scrollTrigger: {
              trigger: splitHero,
              start: "top bottom",
              end: "bottom top",
              scrub: 1.15
            }
          }
        );
      }
      if (featured) {
        gsap.from(featured, {
          x: 48,
          opacity: 0,
          duration: 1.15,
          ease: "power3.out",
          scrollTrigger: {
            trigger: featured,
            start: "top 80%",
            toggleActions: "play none none reverse"
          }
        });
      }
    }

    gsap.utils.toArray(".product-grid .card-wrapper").forEach((el, i) => {
      if (el.closest(".custom-swiper")) return;
      gsap.from(el, {
        y: 36,
        opacity: 0,
        duration: 0.8,
        delay: (i % 4) * 0.06,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 92%",
          toggleActions: "play none none reverse"
        }
      });
    });

    gsap.utils
      .toArray(".rich-text .rich-text__blocks, .image-with-text .image-with-text__content")
      .forEach((el) => {
        gsap.from(el, {
          y: 48,
          opacity: 0,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 85%",
            toggleActions: "play none none reverse"
          }
        });
      });

    gsap.utils
      .toArray(
        ".image-with-text .image-with-text__media img, .image-with-text .image-with-text__media-item img"
      )
      .forEach((el) => {
        gsap.from(el, {
          scale: 1.1,
          opacity: 0.4,
          duration: 1.25,
          ease: "power2.out",
          scrollTrigger: {
            trigger: el,
            start: "top 90%",
            toggleActions: "play none none reverse"
          }
        });
      });

    const footer = document.querySelector(".footer, footer.footer");
    if (footer) {
      gsap.from(footer.querySelectorAll(".footer__content-top, .footer-block, .footer__content-bottom"), {
        y: 28,
        opacity: 0,
        duration: 0.85,
        stagger: 0.08,
        ease: "power2.out",
        scrollTrigger: {
          trigger: footer,
          start: "top 92%",
          toggleActions: "play none none reverse"
        }
      });
    }
  }

  function initLinkUnderlines() {
    const excludeClosest =
      "header, .header, .menu-drawer, .mega-menu, .card-wrapper, .card, .product-card-wrapper, .slider-buttons, .pagination, .hero-banner-slider__buttons, .slideshow__controls, .shopify-payment-button, .predictive-search, .share-button, .product__media-wrapper, .thumbnail-list, .disclosure, .modal-overlay, .cart-notification-wrapper, .announcement-bar, .split-hero-image-wrapper, .intro-categories__item, .button--gsap-invert, .quantity, .facets, .mobile-facets, .complementary-products, .quick-add, .footer-premium, .brand-index";
    const excludeSelf =
      ".button, .button--primary, .button--secondary, .full-unstyled-link, .header__icon, .header__heading-link, .swiper-button-next, .swiper-button-prev, .card__heading, .icon-wrap, .list-social__link, .iubenda-cs-preferences-link, .footer__logo-link, .no-hover";
    const persistentSelectors =
      ".underlined-link, .product-metadata-accordions__file-link, .rte a, .inline-richtext a, .rich-text__text a, .image-with-text__text a, .article-template__content a, .product__description a, .customer a, .collection__view-all-link, .featured-collection__view-all-link";

    document.querySelectorAll("a[href]").forEach((link) => {
      if (link.dataset.linkHover === "true") return;
      if (link.matches(excludeSelf)) return;
      if (link.closest(excludeClosest)) return;
      if (link.querySelector("img, video, picture, svg.icon, .icon")) return;

      const text = (link.textContent || "").replace(/\s+/g, " ").trim();
      if (!text) return;

      const rect = link.getBoundingClientRect();
      if (rect.height > 64 && link.querySelector("div, section, article")) return;

      const hadUnderline =
        link.matches(persistentSelectors) ||
        Boolean(link.closest(".rte, .inline-richtext, .customer")) ||
        (window.getComputedStyle(link).textDecorationLine || "").includes("underline");

      link.classList.add("semenzato-link-hover");
      if (hadUnderline) {
        link.classList.add("semenzato-link-hover--persistent");
        link.addEventListener("mouseenter", () => {
          link.style.animation = "none";
          void link.offsetWidth;
          link.style.animation = "semenzato-underline-reveal 0.45s cubic-bezier(0.22, 1, 0.36, 1) forwards";
        });
        link.addEventListener("mouseleave", () => {
          link.style.animation = "none";
          link.style.backgroundSize = "100% 1px";
        });
      }
      link.dataset.linkHover = "true";
    });
  }

  document.addEventListener("DOMContentLoaded", () => {
    initLinkUnderlines();

    whenGsapReady((gsap, ScrollTrigger) => {
      const bootButtons = () => initInvertButtons(gsap);
      bootButtons();
      initHeroEntrance(gsap);
      initScrollMotions(gsap, ScrollTrigger);

      document.addEventListener("shopify:section:load", () => {
        bootButtons();
        initLinkUnderlines();
      });
      document.addEventListener("shopify:section:reorder", () => {
        bootButtons();
        initLinkUnderlines();
      });
      document.addEventListener("cart:updated", bootButtons);
    });
  });
})();
